import React from 'react'
import { useRef, useState } from 'react'
import { styled } from 'styled-components'

import {
	PiArrowCounterClockwiseBold,
	PiArrowClockwiseBold,
	PiPlayFill,
	PiPauseFill,
} from 'react-icons/pi'
import { GrFormClose } from 'react-icons/gr'

import ConnectedVideo from '../../../basics/ConnectedVideo'
import { IconButton } from '../../../basics/Button'
import type { Image, Video, Audio } from '@mission.io/mission-toolkit/actions'

/**
 * A modal popup that displays a video, image, or audio file.
 */
export function MediaPopup({
	media,
	onClose,
}: {
	media: Image | Video | Audio,
	onClose: () => mixed,
}): React$Node {
	return (
		<Overlay onClick={() => onClose()}>
			<PopupContainer>
				{media.type === 'VIDEO' ? (
					<ControllableVideo src={media.url} />
				) : media.type === 'IMAGE' ? (
					<ImageFillContainer src={media.url} />
				) : media.type === 'AUDIO' ? (
					<AudioFitInContainer controls autoPlay src={media.url} />
				) : null}
				<CloseButton onClick={() => onClose()}>
					<GrFormClose />
				</CloseButton>
			</PopupContainer>
		</Overlay>
	)
}

const CloseButton = styled(IconButton)`
	position: absolute;
	top: 0;
	right: 0;
	background-color: ${({ theme }) => theme.white};
	color: ${({ theme }) => theme.black};
	box-shadow: 1px 1px 10px 0 rgba(255, 255, 255, 0.5);
	&:hover {
		box-shadow: 3px 3px 15px 0 rgba(255, 255, 255, 0.3);
	}

	svg path {
		stroke: currentColor;
	}
`

/**
 * A video component that can be controlled by the user. Includes a play/pause button,
 * as well as buttons to skip forward and backward.
 */
export function ControllableVideo({
	src,
	className,
	onLoad,
	subscribe,
	autoPlay = true,
}: {
	src: string,
	className?: string,
	onLoad?: () => void,
	subscribe?: ({ current: HTMLVideoElement | void }) => void,
	autoPlay?: boolean,
}): React$Node {
	const [playing, setPlaying] = useState(autoPlay === false ? null : true)
	const ref = useRef()

	return (
		<VideoContainer className={className}>
			{/* $FlowExpectedError[incompatible-use] styled components does not type refs correctly when the component being styled is a component using forwardRef */}
			<ConnectedVideoFillContainer
				loop={autoPlay}
				autoPlay={autoPlay}
				onLoadedData={() => {
					subscribe?.(ref)
					onLoad?.()
				}}
				ref={ref}
				url={src}
				onPlay={() => setPlaying(true)}
				onPause={() => setPlaying(false)}
			/>
			<VideoControlsContainer onClick={e => e.stopPropagation()}>
				<IconButton
					onClick={() => {
						if (ref.current) {
							ref.current.currentTime = ref.current.currentTime - 10
						}
					}}>
					<PiArrowCounterClockwiseBold />
				</IconButton>
				<LargeIconButton
					onClick={() => {
						if (!ref.current) {
							return
						}

						if (ref.current.paused) {
							ref.current.play()
						} else {
							ref.current.pause()
						}
					}}>
					{playing ? <PiPauseFill /> : <PiPlayFill />}
				</LargeIconButton>
				<IconButton
					onClick={() => {
						if (ref.current) {
							ref.current.currentTime = ref.current.currentTime + 10
						}
					}}>
					<PiArrowClockwiseBold />
				</IconButton>
			</VideoControlsContainer>
		</VideoContainer>
	)
}

const Overlay = styled.div`
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background-color: rgba(0, 0, 0, 0.5);
	z-index: 20;
`

const PopupContainer = styled.div`
	position: fixed;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 90%;
	height: 90%;
	display: flex;
	justify-content: center;
	align-items: center;
`

const VideoContainer = styled.div`
	position: relative;
	width: 100%;
	height: 100%;

	${IconButton} {
		opacity: 0;
	}

	&:hover ${IconButton} {
		opacity: 1;
	}
`
const VideoControlsContainer = styled.div`
	position: absolute;
	left: 50%;
	bottom: 0;
	transform: translateX(-50%);
	display: flex;
	flex-direction: row;
	justify-content: center;
`

const ImageFillContainer = styled.img`
	width: 100%;
	height: 100%;
	object-fit: contain;
`

const AudioFitInContainer = styled.audio`
	width: 70%;
`

const ConnectedVideoFillContainer = styled(ConnectedVideo)`
	width: 100%;
	height: 100%;
	object-fit: contain;
`

const LargeIconButton = styled(IconButton)`
	font-size: 3em;
`
